import { LeftOutlined } from '@ant-design/icons';
import { Button, Empty, Modal } from 'antd';
import Screen from 'components/BusinessLogic/ScreenTemplates/LoggedScreen';
import ModalCreateComposition from 'components/Modals/CreateComposition';
import ImageModal from 'components/organisms/ImageModal';
import { useImageModal } from 'components/organisms/ImageModal/useImageModal';
import { Loader } from 'components/Wired/DefaultLoader/styles';
import ProductOffer from 'model/ProductOffer';
import { useCallback, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ProducerOfferDetails from 'screens/OfferDetails/components/ProductOfferDetails/ProductOfferDetails';

import { useDispatch, useSelector } from 'react-redux';
import { getPageProductList } from 'services/config';
import {
  useGetDeleteCartMutation,
  useGetOfferByIdQuery,
} from 'services/ecommerceApi';
import { cartSelectors, sessionSelectors } from 'store/state/selectors';

import Packing from 'model/Packing';
import { showAlertError } from 'services/alertService';

import ModalExpiredItemsInCart from 'components/Modals/ExpiredItemsInCart';
import DeliveryDate from 'components/UI/DeliveryDate';
import { isEmpty } from 'lodash';
import moment, { Moment } from 'moment';
import { deliveryDatePattern } from 'services/deliveryDatePattern';
import offerDetailsStore from 'store/state/offerDetails/offerDetailsStore';
import { useShallow } from 'zustand/react/shallow';
import GfpOfferFilter from './components/GfpOfferFilter/GfpOfferFilter';
import GfpOfferHeader from './components/GfpOfferHeader/GfpOfferHeader';
import GfpOfferList from './components/GfpOfferList/GfpOfferList';

import * as S from './OfferDetails.styled';

const redirectToHome = (producerUrl: string): string =>
  `/${producerUrl}/products`;

export default function OfferDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const isExclusiveOffer = location.state?.isExclusiveOffer || false;
  const navigationAuctionDate = location.state?.lkpAuctionDate;
  const producerUrl = useSelector(sessionSelectors.getProducerUrl);
  const { offerId, offerType, packingSelectedId } = useParams<{
    offerId: string;
    offerType: string;
    packingSelectedId: string;
  }>();
  const { isOpen, modalProps, closeModal, openModal } = useImageModal();
  const [productOffer, setProductOffer] = useState<ProductOffer>();
  const [selectedAuctionDate, setSelectedAuctionDate] = useState<
    Date | undefined
  >(navigationAuctionDate);
  const [page] = useState<number>(getPageProductList() ?? 1);
  const customerId = useSelector(sessionSelectors.getSelectedCustomerId);
  const { customers } = useSelector(sessionSelectors.getRoot);
  const isAuthenticated = useSelector(sessionSelectors.getAuthenticated);
  const lkpOfferId = useSelector(cartSelectors.getLkpOfferId);
  const volOfferId = useSelector(cartSelectors.getVolOfferId);
  const selectedCustomerId = useSelector(
    sessionSelectors.getSelectedCustomerId,
  );

  const {
    setShippingFee,
    showModalExpiredItemsInCart,
    setShowModalExpiredItemsInCart,
    billingDateValue,
    setBillingDateValue,
    deliveryDateValue,
    setDeliveryDateValue,
    displayModalInformation,
    setDisplayModalInformation,
    displayModalInformationComposition,
    setDisplayModalInformationComposition,
    handleOnConfirmationDeliveryDate,
    handleOnConfirmationDeliveryDateComposition,
  } = offerDetailsStore(
    useShallow((state) => ({
      setShippingFee: state.setShippingFee,
      showModalExpiredItemsInCart: state.showModalExpiredItemsInCart,
      setShowModalExpiredItemsInCart: state.setShowModalExpiredItemsInCart,
      billingDateValue: state.billingDateValue,
      setBillingDateValue: state.setBillingDateValue,
      deliveryDateValue: state.deliveryDateValue,
      setDeliveryDateValue: state.setDeliveryDateValue,
      displayModalInformation: state.displayModalInformation,
      setDisplayModalInformation: state.setDisplayModalInformation,
      displayModalInformationComposition:
        state.displayModalInformationComposition,
      setDisplayModalInformationComposition:
        state.setDisplayModalInformationComposition,
      handleOnConfirmationDeliveryDate: state.handleOnConfirmationDeliveryDate,
      handleOnConfirmationDeliveryDateComposition:
        state.handleOnConfirmationDeliveryDateComposition,
    })),
  );
  const dispatch = useDispatch();

  useEffect(() => {
    const selectedCustomer = customers?.find(
      (customer) => customer.id === selectedCustomerId,
    );
    const hasFee = !isEmpty(
      productOffer?.shippingFeeFilials?.find(
        (shippingFee) =>
          shippingFee.organizationName === selectedCustomer?.branch,
      ),
    );

    setShippingFee(hasFee);
  }, [
    productOffer?.shippingFeeFilials,
    customers,
    selectedCustomerId,
    setShippingFee,
  ]);

  const [getDeleteCart] = useGetDeleteCartMutation();

  const [packingSelected, setPackingSelected] = useState<Packing>(
    {} as Packing,
  );

  const onCloseModalComposition = () => {
    setModalIsVisibleComposition(false);
  };

  const [productOfferOpened, setProductOfferOpened] = useState<
    ProductOffer | undefined
  >(undefined);
  const [modalIsVisibleComposition, setModalIsVisibleComposition] =
    useState(false);
  const [offerIdModalComposition, setOfferIdModalComposition] = useState<
    number | undefined
  >();

  const onShowModalComposition = (
    productOfferSelected: ProductOffer,
    packingSelected: Packing,
  ) => {
    setModalIsVisibleComposition(true);
    setProductOfferOpened(productOfferSelected);
    setPackingSelected(packingSelected);
  };

  const {
    data: offerWithBox,
    isError,
    isLoading,
    isFetching,
  } = useGetOfferByIdQuery(
    {
      customerId,
      offerId: Number(offerId),
      offerTypes: [offerType ?? ''],
      ...(productOffer?.isLkpOffer ? { packingId: packingSelectedId } : {}),
    },
    {
      skip: !offerId,
    },
  );

  useEffect(() => {
    if (isError) {
      showAlertError(
        dispatch,
        'Não foi possível carregar a oferta no momento, por favor tente novamente mais tarde.',
      );
    }
  }, [dispatch, isError]);

  useEffect(() => {
    const offers = offerWithBox?.offers;
    if (offers && offers?.length > 0) {
      let selectedOffer = offers[0];

      if (navigationAuctionDate && offers.length > 1) {
        const navigationDateFormatted = moment.utc(navigationAuctionDate).format(
          'YYYY-MM-DD',
        );

        const matchingOffer = offers.find((offer) => {
          const offerDateFormatted = moment.utc(offer.lkpAuctionDate).format(
            'YYYY-MM-DD',
          );
          return offerDateFormatted === navigationDateFormatted;
        });

        if (matchingOffer) {
          selectedOffer = matchingOffer;
        }
      }

      setProductOffer(selectedOffer);

      if (selectedOffer.isLkpOffer && selectedOffer.lkpAuctionDate) {
        setSelectedAuctionDate(selectedOffer.lkpAuctionDate);
      }
    } else {
      setProductOffer(undefined);
    }
  }, [offerWithBox, offerId, navigationAuctionDate]);

  const handleClickButtonGoToCart = () => {
    navigate('/shopping-cart');
    setShowModalExpiredItemsInCart(false);
  };

  const handleClickButtonDeleteCart = useCallback(() => {
    const cartId = productOffer?.isLkpOffer ? lkpOfferId : volOfferId;
    getDeleteCart({ cartId });
    setShowModalExpiredItemsInCart(false);
  }, [
    getDeleteCart,
    productOffer?.isLkpOffer,
    lkpOfferId,
    volOfferId,
    setShowModalExpiredItemsInCart,
  ]);

  const clickCloseModalInformation = () => {
    setDisplayModalInformation(false);
  };

  const clickCloseModalInformationComposition = () => {
    setDisplayModalInformationComposition(false);
  };

  const disableDatesDelivery = (value: Moment) => {
    const calendarValue = moment(value).set({
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 0,
    });
    const dateNow = moment().set({
      hour: 0,
      minute: 0,
      second: 1,
      millisecond: 0,
    });
    const endDateFormatted = moment(productOffer?.endDate).set({
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 0,
    });

    const deliveryDays = productOffer?.siteDeliveryPatterns.map(
      (date) => date.deliveryDayNumber,
    );
    const deliveryDate = deliveryDatePattern(
      productOffer?.siteDeliveryPatterns ?? [],
    ).getDeliveryDateByDatePatterns(dateNow.toDate().getDay());
    return (
      calendarValue.isBefore(
        moment(productOffer?.startDate).isAfter(moment())
          ? productOffer?.startDate
          : deliveryDate,
      ) ||
      value.isAfter(endDateFormatted) ||
      !deliveryDays?.includes(calendarValue.day())
    );
  };

  const [showDatePickerDelivery, setShowDatePickerDelivery] =
    useState<boolean>(true);
  const [showDatePickerBilling, setShowDatePickerBilling] =
    useState<boolean>(false);

  const disableDatesBilling = (value: Moment) => {
    if (deliveryDateValue !== null) {
      const initalDay = moment(deliveryDateValue);
      const lastDay = moment(deliveryDateValue).add(2, 'days');

      return value.isAfter(lastDay) || value.isBefore(initalDay);
    }
    return false;
  };

  const handleOnOpenChangeDelivery = (event: boolean) => {
    setShowDatePickerDelivery(event);

    if (deliveryDateValue !== null) {
      setShowDatePickerBilling(true);
    }
  };

  const handleOnOpenChangeBilling = (event: boolean) => {
    setShowDatePickerBilling(event);
  };

  const handleOnChangeDelivery = (value: Moment | null) => {
    setBillingDateValue(null);
    setDeliveryDateValue(value);

    if (value) {
      setShowDatePickerBilling(true);
    }
  };

  const handleOnChangeBilling = (value: Moment | null) => {
    setBillingDateValue(value);
  };

  const title = productOffer?.longName;

  const subHeader = (
    <S.Header>
      <Button
        icon={<LeftOutlined />}
        onClick={() => navigate(`/${producerUrl}/products`)}
      >
        <S.BackText>Voltar às ofertas</S.BackText>
      </Button>
      {title && <S.Title>Detalhes da Oferta: {title}</S.Title>}
    </S.Header>
  );

  const renderProductOffer = () => {
    if (productOffer) {
      return (
        <ProducerOfferDetails
          productOffer={productOffer}
          key={productOffer.id}
          setModalIsVisibleComposition={onShowModalComposition}
          setOfferIdModalComposition={setOfferIdModalComposition}
          onImageClick={openModal}
          isExclusiveOffer={isExclusiveOffer}
        />
      );
    }

    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  };

  const renderGfpDivider = () => {
    if (productOffer && !productOffer.isLkpOffer) return null;
    if (productOffer && productOffer.isLkpOffer) {
      return <S.GfpOfferDivider />;
    }

    return <Empty image={Empty.PRESENTED_IMAGE_DEFAULT} />;
  };

  const renderGfpHeader = () => {
    if (productOffer && productOffer.isLkpOffer) {
      return <GfpOfferHeader siteName={productOffer.siteName} />;
    }

    return <Empty image={Empty.PRESENTED_IMAGE_DEFAULT} />;
  };

  const formattedAuctionDate = moment.utc(
    selectedAuctionDate || productOffer?.lkpAuctionDate,
  ).format('YYYY-MM-DD');

  const renderGfpFilter = () => {
    if (productOffer && productOffer.isLkpOffer) {
      return (
        <GfpOfferFilter
          lkpOfferId={productOffer.offerId}
          offerType={productOffer.offerType}
          packingId={packingSelectedId}
          auctionDate={formattedAuctionDate}
        />
      );
    }

    return <Empty image={Empty.PRESENTED_IMAGE_DEFAULT} />;
  };

  const renderGfpList = () => {
    if (productOffer && productOffer.isLkpOffer) {
      return (
        <GfpOfferList productOffer={productOffer} onImageClick={openModal} />
      );
    }

    return <Empty image={Empty.PRESENTED_IMAGE_DEFAULT} />;
  };

  const renderGfpSection = () => {
    if (productOffer && productOffer.isLkpOffer) {
      return (
        <>
          {renderGfpHeader()}
          {renderGfpFilter()}
          {renderGfpList()}
        </>
      );
    }

    return null;
  };

  const content = (
    <div>
      {subHeader}
      <S.DetailsContainer>
        {renderProductOffer()}
        {renderGfpDivider()}
        {renderGfpSection()}
      </S.DetailsContainer>
    </div>
  );


  const shouldShowLoader = () =>
    (isLoading || isFetching) &&
    isAuthenticated &&
    !customerId &&
    (page === 1 || !isMobile);

  if (shouldShowLoader()) {
    return <Loader spinning size="large" />;
  }

  return (
    <>
      <Screen showSubHeader={false} content={content} />
      <ImageModal {...modalProps} isOpen={isOpen} onClose={closeModal} />
      <ModalCreateComposition
        productOfferOpened={productOfferOpened}
        offerId={offerIdModalComposition}
        setModalIsVisible={onCloseModalComposition}
        modalIsVisible={modalIsVisibleComposition}
        packingSelected={packingSelected}
      />
      <ModalExpiredItemsInCart
        modalIsVisible={showModalExpiredItemsInCart}
        actionButtonCancel={handleClickButtonGoToCart}
        actionButtonOk={handleClickButtonDeleteCart}
      />
      <Modal
        closable
        onCancel={clickCloseModalInformationComposition}
        visible={displayModalInformationComposition}
        width="414px"
        footer={null}
      >
        <S.WarningLabel>
          <b>Atenção!</b> É necessário que selecione uma data para entrega e
          faturamento.
        </S.WarningLabel>
        <DeliveryDate
          initialDatePicker={{
            disabled: false,
            disabledDate: disableDatesDelivery,
            value: deliveryDateValue,
            open: showDatePickerDelivery && displayModalInformationComposition,
            onOpenChange: handleOnOpenChangeDelivery,
            onChange: handleOnChangeDelivery,
          }}
          endDatePicker={{
            disabled: deliveryDateValue === null,
            disabledDate: disableDatesBilling,
            value: billingDateValue,
            open:
              showDatePickerBilling &&
              displayModalInformationComposition &&
              !showDatePickerDelivery,
            onOpenChange: handleOnOpenChangeBilling,
            onChange: handleOnChangeBilling,
          }}
          onConfirmation={handleOnConfirmationDeliveryDateComposition}
          producerUnavailableDates={productOffer?.unavailableDates || []}
        />
      </Modal>
      <Modal
        closable
        onCancel={clickCloseModalInformation}
        visible={displayModalInformation}
        width="414px"
        footer={null}
      >
        <S.WarningLabel>
          <b>Atenção!</b> É necessário que selecione uma data para entrega e
          faturamento.
        </S.WarningLabel>
        <DeliveryDate
          initialDatePicker={{
            disabled: false,
            disabledDate: disableDatesDelivery,
            value: deliveryDateValue,
            open: showDatePickerDelivery && displayModalInformation,
            onOpenChange: handleOnOpenChangeDelivery,
            onChange: handleOnChangeDelivery,
          }}
          endDatePicker={{
            disabled: deliveryDateValue === null,
            disabledDate: disableDatesBilling,
            value: billingDateValue,
            open:
              showDatePickerBilling &&
              displayModalInformation &&
              !showDatePickerDelivery,
            onOpenChange: handleOnOpenChangeBilling,
            onChange: handleOnChangeBilling,
          }}
          onConfirmation={handleOnConfirmationDeliveryDate}
          producerUnavailableDates={productOffer?.unavailableDates || []}
        />
      </Modal>
      {shouldShowLoader() && <Loader spinning size="large" />}
    </>
  );
}
